import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import type { QueryClient } from '@tanstack/react-query';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { AgentModel } from './agent.model';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { isSaveArtifactsApplicable } from '@cotera/client/v0/chat/artifact-dirty-state';

/**
 * Prefer an explicit id, then the targeted agent, then the sole in-scope agent.
 * The last fallback covers edge cases where targeting has not been applied yet
 * (e.g. brief windows around stack mount).
 */
function resolveAgentModel(
  context: Pick<
    ExecuteContext,
    'getInScopeModelOfType' | 'targetedModelOfType' | 'getInScopeModelsOfType'
  >,
  agentId?: string
): AgentModel | null {
  if (agentId !== undefined) {
    return context.getInScopeModelOfType(AgentModel, agentId);
  }
  const targeted = context.targetedModelOfType(AgentModel);
  if (targeted !== null) {
    return targeted;
  }
  const agents = context.getInScopeModelsOfType(AgentModel);
  if (agents.length === 1) {
    return agents[0] ?? null;
  }
  return null;
}

type Params = {
  silent?: boolean;
  /** When set (e.g. from Add Resource flow), passed to backend to assign agent to folder after create. */
  folderId?: string;
  /** Persisted atomically with the create so the broadcast carries the right emoji. */
  emoji?: string | null;
  /**
   * Resolve the model to save by id (silent autosave / multi-artifact save)
   * instead of the targeted one. Needed when several agents share one actions
   * scope, where "targeted" is ambiguous. Falls back to targeted when unset.
   */
  agentId?: string;
};

export class SaveAgentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Agent';
  description = 'Save agent changes';
  inputSchema = z.object({
    silent: z.boolean().optional(),
    folderId: z.string().uuid().optional(),
    emoji: z.string().nullable().optional(),
    agentId: z.string().optional(),
  });
  shortcut = ['ctrl', 's'];
  group = 'Agent';
  icon = 'save';

  private _lastSavedAgentId: string | null = null;
  private _lastSaveWasNew: boolean = false;
  /** Autosaves / navigation saves pass silent:true; we don't track those. */
  private _lastSaveWasSilent: boolean = false;

  constructor(
    private readonly client: TenantedClient['resource']['agent'],
    private readonly queryClient: QueryClient,
    private readonly navigate: (
      path: string,
      options?: { replace?: boolean }
    ) => void,
    private readonly orgSlug: string,
    private readonly defaultFolderId?: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    // Chat-level Save Artifacts owns Ctrl+S when any open artifact is dirty.
    if (isSaveArtifactsApplicable(context)) {
      return false;
    }
    return resolveAgentModel(context) !== null;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = resolveAgentModel(context, params.agentId);
    if (agent === null) {
      return ok({});
    }

    this._lastSaveWasNew = agent.isNew;
    const silent = params.silent === true;
    this._lastSaveWasSilent = silent;
    const saveToast = silent
      ? null
      : toast.loading('Saving agent...', { id: 'save-agent' });

    try {
      const folderId = params.folderId ?? this.defaultFolderId;
      const result = agent.isNew
        ? await this.client.create({
            editorState: agent.getEditorStateForSave(),
            model: agent.getModelForSave(),
            id: agent.agentId,
            ...(folderId !== undefined && { folderId }),
            ...(params.emoji !== undefined && { emoji: params.emoji }),
          })
        : await this.client.update({
            id: agent.agentId,
            // Only re-send editorState when the body actually changed. A
            // model-only save otherwise round-trips the raw persisted document,
            // which can be a legacy shape the strict update schema rejects
            // (ApiSchemaValidationError). Omitting it keeps the existing state.
            ...(agent.editorStateChangedFromServer()
              ? { editorState: agent.getEditorStateForSave() }
              : {}),
            model: agent.getModelForSave(),
          });

      const updated = Assert.assertOk(result);
      this._lastSavedAgentId = updated.id;
      const savedEditorState = agent.getEditorStateForSave();
      agent.markAsSaved(savedEditorState);
      if (!silent && saveToast !== null) {
        saveToast.success(`Agent ${updated.title ?? 'Untitled'} saved`);
      }
      const resourceQueryKey = queryKeys.resource.get({
        orgId: updated.orgId,
        type: ResourceType.Agent,
        id: updated.id,
      });
      await this.queryClient.invalidateQueries({ queryKey: resourceQueryKey });
      void (async () => {
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.agents.all({ orgId: updated.orgId }),
        });
        await this.queryClient.invalidateQueries({
          queryKey: [updated.orgId, 'resource', 'list', ResourceType.Agent],
        });
        await this.queryClient.invalidateQueries({
          queryKey: queryKeys.toolsV2.all({ orgId: updated.orgId }),
        });
        await this.queryClient.refetchQueries({
          queryKey: queryKeys.agents.all({ orgId: updated.orgId }),
        });
        await this.queryClient.refetchQueries({
          queryKey: queryKeys.toolsV2.all({ orgId: updated.orgId }),
        });
      })();

      return ok({});
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save agent';
      if (!silent && saveToast !== null) {
        saveToast.error(message);
      }
      return err([{ t: 'error', message }]);
    }
  }

  track(): ActionTrackMetadata | null {
    // Skip autosaves / navigation saves — only track deliberate user saves.
    if (this._lastSavedAgentId === null || this._lastSaveWasSilent) {
      return null;
    }
    return {
      event: CocoEvent.AgentSaved,
      properties: {
        agentId: this._lastSavedAgentId,
        isNew: this._lastSaveWasNew,
      },
    };
  }
}
